import {Component, OnInit, AfterViewInit, ElementRef, ViewChildren, QueryList, inject} from '@angular/core';
import {Subject, takeUntil, Subscription, firstValueFrom} from 'rxjs';
import {FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {Router, RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {MaterialModule} from '@app/material.module';
import {AuthService} from '@services/api/auth/auth.service';
import {TokenStorageService} from '@services/local-storage/token-storage/token-storage.service';
import {SharedDataService} from '@services/share-data/share-data.service';
import {UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import {MessagesService} from '@services/messages/messages.service';
import {environment} from '@environment/environment';
import { MaskEmailPipe} from '@pipe/mask-email.pipe';

@Component({
  selector: 'app-verify-otp-login',
  imports: [MaterialModule, ReactiveFormsModule, CommonModule, TranslateModule, MaskEmailPipe, RouterModule],
  standalone: true,
  templateUrl: './verify-otp-login.component.html',
  styleUrl: './verify-otp-login.component.scss'
})
export class VerifyOtpLoginComponent implements OnInit, AfterViewInit {
  verifyOtpForm: FormGroup;
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  otpLength = 6;
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private auth_service = inject(AuthService);
  private token_storage_service = inject(TokenStorageService);
  private share_data_service = inject(SharedDataService);
  private user_storage_service = inject(UserStorageService);
  protected email: string = '';
  private emailSubscription: Subscription | undefined;
  private errorMessage: string = '';
  private messageService = inject(MessagesService);

  constructor(private fb: FormBuilder) {
    this.verifyOtpForm = this.fb.group({
      otp: this.fb.array(Array(this.otpLength).fill('').map(() =>
        this.fb.control('', [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(1),
          Validators.pattern('[0-9]')
        ])
      )),
    });
  }

  async ngOnInit(): Promise<void> {
    this.emailSubscription = this.share_data_service.data$.subscribe(email => {
      if (email) {
        this.email = email;
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.focusFirstInput();
    });
  }

  get otpControls() {
    return (this.verifyOtpForm.get('otp') as FormArray).controls;
  }

  focusFirstInput(): void {
    if (this.otpInputs && this.otpInputs.length > 0) {
      this.otpInputs.first.nativeElement.focus();
    }
  }

  onInputChange(index: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    if (!/^\d$/.test(value)) {
      inputElement.value = '';
      return;
    }

    this.verifyOtpForm.get(['otp', index])?.setValue(value);

    if (index < this.otpLength - 1) {
      this.otpInputs.get(index + 1)?.nativeElement.focus();
    }

    inputElement.value = value.charAt(0);
  }

  onKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && index > 0 && this.otpInputs.get(index)?.nativeElement.value === '') {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }
  }

  resendOtp(): void {
    if (this.email) {
      this.verifyOtpForm.reset();
      this.auth_service
        .resend_otp(
          this.email
        )
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.success('success', response.data.toString(), 5000);
            }
          },
          error: (error) => {
          }
        });
    } else {
      this.messageService.error('error', "no_email", 5000);
    }
  }

  async storeUser(): Promise<void> {
    try {
      const userData = await firstValueFrom(this.auth_service.get_user_data());
      await this.user_storage_service.setUserStorage(userData.data);
    } catch (error) {
      throw error;
    }
  }


  onSubmit(): void {
    if (this.verifyOtpForm.valid) {
      const otpValue = this.verifyOtpForm.value.otp.join('');
      this.auth_service.verify_otp(this.email, otpValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (response) => {
            try {
              await this.token_storage_service.setTokenStorage(response.data.token);
              await this.storeUser();
              this.router.navigate([environment.home_page]);
            } catch (error) {

            }
          },
          error: (error) => {
            this.verifyOtpForm.reset();
            this.errorMessage = error?.error?.error || 'Verifica OTP fallita. Riprova.';
          }
        });
    } else {
      // Se il form non è valido, segna tutti i campi come toccati
      this.verifyOtpForm.markAllAsTouched();
      this.verifyOtpForm.reset();
    }
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
